"use client";

import React, { Suspense, useCallback, useRef, useState } from "react";
import { StyledDiv } from "./widgetCatalog.styles";
import { IoMdClose } from "@react-icons/all-files/io/IoMdClose";
import { AiFillStar } from "@react-icons/all-files/ai/AiFillStar";
import { AiFillFire } from "@react-icons/all-files/ai/AiFillFire";
import { RiLeafFill } from "@react-icons/all-files/ri/RiLeafFill";
import Image from "next/image";
import Link from "next/link";

import type {
  ICatalogApp,
  ICatalogPage,
  ICatalogTaxonomy,
  IPlatformPageData,
} from "../../helpers/catalogData";
import { Button } from "../button/button.comp";
import { useRouter, useSearchParams } from "next/navigation";
import { debounce } from "lodash";

interface IWidgetCatalogProps {
  apps: ICatalogApp[];
  pages?: ICatalogPage[];
  platform?: Pick<IPlatformPageData, "slug" | "title">;
  hideHome?: boolean;
  categories: ICatalogTaxonomy[];
  tags: ICatalogTaxonomy[];
}

export const WidgetCatalog = ({
  apps,
  platform,
  pages,
  hideHome = false,
  categories,
  tags,
}: IWidgetCatalogProps) => {
  return (
    <StyledDiv>
      <Suspense>
        <Catalog
          apps={apps}
          platform={platform}
          pages={pages}
          hideHome={hideHome}
          categories={categories}
          tags={tags}
        />
      </Suspense>
    </StyledDiv>
  );
};

function Catalog({
  apps,
  platform,
  pages,
  hideHome = false,
  categories,
  tags,
}: IWidgetCatalogProps) {
  const searchParams = useSearchParams();
  const searchQuery = searchParams?.get("search") || "";
  const categoryQuery = searchParams?.get("category") || "";
  const router = useRouter();
  const listRef = useRef<any>();
  const [filter, setFilter] = useState({
    page: "",
    name: searchQuery || "",
    tag: "",
    cat: categoryQuery || "",
  });
  const pluginList = apps.sort((a, b) => a.order - b.order);
  const searchText = filter.name?.toLowerCase() || "";

  const tagList = pluginList.reduce((acc: string[], plugin: ICatalogApp) => {
    (plugin.newTags || []).forEach((tag) => {
      const wantedTag = tags.find((t) => t._id === tag._id);
      if (wantedTag && !acc.includes(wantedTag.name)) {
        acc.push(wantedTag.name);
      }
    });
    return acc;
  }, []);

  const catList = pluginList.reduce((acc: any, plugin: ICatalogApp) => {
    (plugin.newCategories || []).forEach((category) => {
      const wantedCat = categories.find((c) => c._id === category._id);

      if (wantedCat && !acc[wantedCat.name]) acc[wantedCat.name] = 1;
      else acc[wantedCat?.name as string] = acc[wantedCat?.name as string] + 1;
    });
    return acc;
  }, {});

  const weightedSearch = () => {
    return pluginList
      .map((plugin) => {
        let score = 0;

        // Assign scores based on match
        if (plugin.name?.toLowerCase().includes(searchText)) {
          score += 3; // Higher weight for name matches
        }
        if (plugin.teaser?.toLowerCase().includes(searchText)) {
          score += 2; // Medium weight for teaser matches
        }
        if (plugin.description?.toLowerCase().includes(searchText)) {
          score += 1; // Lower weight for description matches
        }
        if (
          plugin.newCategories?.some(
            (c) => c.name?.toLowerCase() === searchText?.toLowerCase()
          )
        ) {
          score += 3;
        }

        return { plugin, score };
      })
      .filter(({ score }) => score > 0) // Filter out plugins with no match
      .sort((a, b) => b.score - a.score) // Sort by score in descending order
      .map(({ plugin }) => plugin); // Return the sorted plugins
  };

  const filteredPlugins = weightedSearch()
    .filter((p) =>
      filter.tag ? (p.newTags || []).map((t) => t.name).includes(filter.tag) : p
    )
    .filter((p) =>
      filter.cat
        ? (p.newCategories || [])
            .map((t) => t.name?.toLowerCase())
            .includes(filter.cat.toLowerCase())
        : p
    );

  const debouncedSearch = useCallback(
    debounce((val: string) => {
      const baseUrl = platform?.slug
        ? `/platforms/${platform.slug}`
        : "/widgets";
      const url = `${baseUrl}?search=${encodeURIComponent(val)}`;
      router.replace(url);
    }, 300), // 300ms delay
    [router]
  );

  function handleSearchChange(value: string) {
    setFilter((prevFilter) => ({
      ...prevFilter,
      name: value,
    }));
    debouncedSearch(value);
  }

  function renderSidebar() {
    return (
      <div className="filter-container">
        <form
          className="search-components"
          onSubmit={(e) => e.preventDefault()}
        >
          <input
            name="searchType"
            title="Search Widgets"
            placeholder="Search widgets..."
            value={filter.name}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {filter.name && (
            <IoMdClose
              onClick={() => handleSearchChange("")}
              className="reset"
              fontSize={20}
            />
          )}
        </form>
        <div className="categories">
          {!hideHome && (
            <>
              <button
                className={`cat ${filter.page === "home" ? "active" : ""}`}
                onClick={() =>
                  setFilter({
                    ...filter,
                    cat: "",
                    tag: "",
                    name: "",
                    page: "home",
                  })
                }
              >
                Home
              </button>
              <div className="divider"></div>
            </>
          )}
          <p className="cat-title">Categories</p>
          <button
            className={`cat ${
              !filter.page && !filter.tag && !filter.cat ? "active" : ""
            }`}
            onClick={() =>
              setFilter({ ...filter, page: "", cat: "", tag: "", name: "" })
            }
          >
            All ({pluginList.length})
          </button>
          {Object.keys(catList).map((cat) => (
            <button
              key={cat}
              className={`cat ${
                !filter.page && filter.cat?.toLowerCase() === cat.toLowerCase()
                  ? "active"
                  : ""
              }`}
              onClick={() => {
                setFilter({ ...filter, page: "", tag: "", name: "", cat });
                if (listRef.current && filter.cat) {
                  listRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }
              }}
            >
              {cat} ({catList[cat]})
            </button>
          ))}
        </div>
      </div>
    );
  }

  function renderApps(appsList: ICatalogApp[]) {
    if (pluginList.length === 0) {
      return <div className="apps-container">Loading...</div>;
    }

    return (
      <div ref={listRef} className={`apps-container`}>
        {appsList.length > 0 && (
          <>
            {!filter.name && !filter.cat && !filter.tag && (
              <Link
                title={`Embeddable - AI Widget Builder`}
                className="app embd-promo"
                href="https://embeddable.co?ref=commonninja"
                target="_blank"
              >
                <div className="new-ribbon">NEW</div>
                <Image
                  src="/assets/embeddable-banner.png"
                  alt="Embeddable - AI Widget Builder"
                  width={320}
                  height={170}
                />
                <div className="details">
                  <h3>AI Widget Builder</h3>
                  <p>
                    Create any custom widget you can imagine with the power of
                    AI. No coding required - just describe what you need and
                    watch it come to life!
                  </p>
                </div>
              </Link>
            )}
            {appsList.map((plugin: ICatalogApp) => {
              let path = `/widgets/${plugin.seoSlug || plugin.slug}`;

              if (platform) {
                const pageExists = (pages || []).some(
                  (page) => page.appId === plugin._id
                );

                path = pageExists
                  ? `/widgets/${plugin.seoSlug || plugin.slug}/${platform?.slug}`
                  : `/widgets/${plugin.seoSlug || plugin.slug}`;
              }

              return (
                <Link
                  title={`${plugin.name} ${
                    platform?.title ? `for ${platform?.title}` : ""
                  }`}
                  key={plugin.name}
                  className="app"
                  href={path}
                >
                  <Image
                    src={plugin.catalogImage}
                    alt={`${plugin.name} ${
                      platform?.title ? `for ${platform?.title}` : ""
                    } logo`}
                    width={320}
                    height={170}
                  />
                  <div className="details">
                    <h3>
                      {plugin.name}{" "}
                      {platform?.title ? `for ${platform?.title}` : ""}
                    </h3>
                    <p>{plugin.description || plugin.teaser}</p>
                  </div>
                </Link>
              );
            })}
          </>
        )}
        {!appsList.length && (
          <div className="no-items embd-promo">
            <div className="promo-banner">
              <div className="promo-icon">
                <Image
                  src="/assets/icon.png"
                  alt="Common Ninja icon"
                  width={100}
                  height={100}
                />
              </div>
            </div>
            <div className="promo-content">
              <h2>Couldn&apos;t find what you&apos;re looking for?</h2>
              <h3>Try Embeddable - Our AI Widget Builder</h3>
              <p>
                Create any custom widget you can imagine with the power of AI.
                No coding required - just describe what you need and watch it
                come to life!
              </p>
              <div className="promo-features">
                <span>🎨 Fully Customizable</span>
                <span>⚡ Instant Creation</span>
                <span>🚀 Easy Integration</span>
              </div>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  window.open("https://embeddable.co?ref=commonninja");
                }}
                style={{
                  fontSize: 16,
                  padding: "14px 32px",
                }}
              >
                Try Embeddable Now →
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderHome() {
    return (
      <div className="widget-catalog-home">
        {tagList.map((tag) => {
          return (
            <div className="home-section" key={tag}>
              <h3>
                {tag.toLowerCase().includes("popular") && (
                  <AiFillStar className="icon" />
                )}
                {tag.toLowerCase().includes("trending") && (
                  <AiFillFire className="icon" />
                )}
                {tag.toLowerCase().includes("new") && (
                  <RiLeafFill className="icon" />
                )}
                {tag}
              </h3>
              {renderApps(
                pluginList.filter((p) =>
                  (p.newTags || []).map((t) => t.name).includes(tag)
                )
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="inner">
      <div className="main-content-wrapper">
        {renderSidebar()}
        {filter.page === "home" ? renderHome() : renderApps(filteredPlugins)}
      </div>
    </div>
  );
}
